'use client'

import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { Users, UserPlus, X, Pencil, Clock, Check, Trash2, Loader2 } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  getInvitations,
  revokeInvitation,
  updateMemberRole,
  removeMember,
} from '@/lib/services/teamService'
import type { TeamMember, TeamInvitation } from '@/lib/types/team'
import type { UserSummary } from '@/lib/services/productService'
import { InviteUserDialog } from './invite-user-dialog'

// ============================================================================
// TYPES
// ============================================================================

interface TeamManagementPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productSlug: string
  owner: UserSummary
  members: TeamMember[]
  onTeamUpdate?: () => void
}

// ============================================================================
// COMPONENT
// ============================================================================

export function TeamManagementPanel({
  open,
  onOpenChange,
  productSlug,
  owner,
  members: initialMembers,
  onTeamUpdate,
}: TeamManagementPanelProps) {
  const [members, setMembers] = useState<TeamMember[]>(initialMembers)
  const [invitations, setInvitations] = useState<TeamInvitation[]>([])
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false)
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [editingMemberId, setEditingMemberId] = useState<number | null>(null)
  const [editingRole, setEditingRole] = useState('')
  const [savingRoleFor, setSavingRoleFor] = useState<number | null>(null)
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null)
  const [revokingInvitationId, setRevokingInvitationId] = useState<number | null>(null)

  // Sync members with prop changes
  useEffect(() => {
    setMembers(initialMembers)
  }, [initialMembers])

  // Load invitations when panel opens
  useEffect(() => {
    if (open) {
      loadInvitations()
    }
  }, [open, productSlug])

  const loadInvitations = useCallback(async () => {
    setIsLoadingInvitations(true)
    const result = await getInvitations(productSlug)
    if (result.success && result.data) {
      // Only show pending invitations
      setInvitations(result.data.filter(inv => inv.status === 'pending'))
    }
    setIsLoadingInvitations(false)
  }, [productSlug])

  const handleRevokeInvitation = async (userId: number) => {
    setRevokingInvitationId(userId)
    const result = await revokeInvitation(productSlug, userId)
    if (result.success) {
      setInvitations(prev => prev.filter(inv => inv.invitee.id !== userId))
      toast.success('Приглашение отозвано')
    } else {
      toast.error(result.error?.message || 'Не удалось отозвать приглашение')
    }
    setRevokingInvitationId(null)
  }

  const handleStartEditRole = (member: TeamMember) => {
    setEditingMemberId(member.userId)
    setEditingRole(member.role)
  }

  const handleCancelEditRole = () => {
    setEditingMemberId(null)
    setEditingRole('')
  }

  const handleSaveRole = async (userId: number) => {
    if (!editingRole.trim()) {
      toast.error('Роль не может быть пустой')
      return
    }
    if (editingRole.length > 30) {
      toast.error('Роль не может быть длиннее 30 символов')
      return
    }

    setSavingRoleFor(userId)
    const result = await updateMemberRole(productSlug, userId, editingRole.trim())
    if (result.success && result.data) {
      setMembers(prev =>
        prev.map(m => (m.userId === userId ? { ...m, role: result.data!.role } : m))
      )
      toast.success('Роль обновлена')
      onTeamUpdate?.()
    } else {
      toast.error(result.error?.message || 'Не удалось обновить роль')
    }
    setSavingRoleFor(null)
    setEditingMemberId(null)
    setEditingRole('')
  }

  const handleRemoveMember = async (userId: number) => {
    setRemovingMemberId(userId)
    const result = await removeMember(productSlug, userId)
    if (result.success) {
      setMembers(prev => prev.filter(m => m.userId !== userId))
      toast.success('Участник удален из команды')
      onTeamUpdate?.()
    } else {
      toast.error(result.error?.message || 'Не удалось удалить участника')
    }
    setRemovingMemberId(null)
  }

  const handleInviteSuccess = () => {
    loadInvitations()
    setShowInviteDialog(false)
    toast.success('Приглашение отправлено')
  }

  const pendingInvitations = invitations.filter(inv => inv.status === 'pending')

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Управление командой
            </SheetTitle>
            <SheetDescription>
              Приглашайте участников и управляйте ролями в команде
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-col gap-6 px-4 pb-4">
            {/* Invite Button */}
            <Button onClick={() => setShowInviteDialog(true)} className="w-full">
              <UserPlus className="h-4 w-4 mr-2" />
              Пригласить участника
            </Button>

            {/* Owner Section */}
            <div>
              <h3 className="text-sm font-medium text-foreground-secondary mb-3">
                Владелец
              </h3>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <Avatar className="h-10 w-10">
                  {owner.avatarUrl && (
                    <AvatarImage src={owner.avatarUrl} alt={owner.name} />
                  )}
                  <AvatarFallback>{owner.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{owner.name}</div>
                  {owner.username && (
                    <div className="text-xs text-foreground-tertiary truncate">
                      @{owner.username}
                    </div>
                  )}
                </div>
                <Badge variant="secondary">Владелец</Badge>
              </div>
            </div>

            {/* Team Members Section */}
            <div>
              <h3 className="text-sm font-medium text-foreground-secondary mb-3">
                Участники команды ({members.length})
              </h3>
              {members.length === 0 ? (
                <div className="text-center py-6 text-foreground-tertiary text-sm">
                  В команде пока нет участников
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map(member => (
                    <div
                      key={member.userId}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border"
                    >
                      <Avatar className="h-10 w-10">
                        {member.avatarUrl && (
                          <AvatarImage src={member.avatarUrl} alt={member.name} />
                        )}
                        <AvatarFallback>
                          {member.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{member.name}</div>
                        {editingMemberId === member.userId ? (
                          <div className="flex items-center gap-1 mt-1">
                            <Input
                              value={editingRole}
                              onChange={e => setEditingRole(e.target.value)}
                              className="h-7 text-xs"
                              maxLength={30}
                              placeholder="Роль"
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => handleSaveRole(member.userId)}
                              disabled={savingRoleFor === member.userId}
                            >
                              {savingRoleFor === member.userId ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Check className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={handleCancelEditRole}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-foreground-tertiary">
                              {member.role}
                            </span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-5 w-5"
                              onClick={() => handleStartEditRole(member)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleRemoveMember(member.userId)}
                        disabled={removingMemberId === member.userId}
                      >
                        {removingMemberId === member.userId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Invitations Section */}
            <div>
              <h3 className="text-sm font-medium text-foreground-secondary mb-3">
                Ожидающие приглашения ({pendingInvitations.length})
              </h3>
              {isLoadingInvitations ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-foreground-tertiary" />
                </div>
              ) : pendingInvitations.length === 0 ? (
                <div className="text-center py-6 text-foreground-tertiary text-sm">
                  Нет ожидающих приглашений
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingInvitations.map(invitation => (
                    <div
                      key={invitation.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30"
                    >
                      <Avatar className="h-10 w-10">
                        {invitation.invitee.avatarUrl && (
                          <AvatarImage
                            src={invitation.invitee.avatarUrl}
                            alt={invitation.invitee.name}
                          />
                        )}
                        <AvatarFallback>
                          {invitation.invitee.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {invitation.invitee.name}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-foreground-tertiary">
                          <span>{invitation.role}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Ожидает
                          </span>
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleRevokeInvitation(invitation.invitee.id)}
                        disabled={revokingInvitationId === invitation.invitee.id}
                      >
                        {revokingInvitationId === invitation.invitee.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        productSlug={productSlug}
        existingMemberIds={[owner.id, ...members.map(m => m.userId)]}
        pendingInvitationUserIds={pendingInvitations.map(inv => inv.invitee.id)}
        onSuccess={handleInviteSuccess}
      />
    </>
  )
}
